// src/store/voiceModeStore.ts
// Zustand store for Lex voice mode state management.
// Controls: voice mode toggle, recording state, playback state, VAD, settings.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type RecordingMode = 'toggle' | 'push-to-talk';

export interface VoiceModeState {
  // ── Voice Mode Toggle ──────────────────────────────────────────────
  /** Whether voice mode is active (both STT + TTS) */
  voiceModeEnabled: boolean;
  /** Enable/disable just STT (voice input) */
  sttEnabled: boolean;
  /** Enable/disable just TTS (voice output) */
  ttsEnabled: boolean;

  // ── Recording State ────────────────────────────────────────────────
  /** Whether the mic is currently recording */
  isRecording: boolean;
  /** Recording duration in milliseconds */
  recordingDuration: number;
  /** Audio level for waveform visualization (0-1) */
  audioLevel: number;
  /** Recording mode preference */
  recordingMode: RecordingMode;

  // ── Playback State ─────────────────────────────────────────────────
  /** Whether Lex is currently speaking */
  isSpeaking: boolean;
  /** Playback progress (0-1) */
  playbackProgress: number;

  // ── VAD (Voice Activity Detection) ─────────────────────────────────
  /** Whether VAD auto-listen is enabled (auto-record after Lex speaks) */
  vadEnabled: boolean;

  // ── Service Status ─────────────────────────────────────────────────
  /** Whether the Whisper STT service is available */
  whisperReady: boolean;
  /** Whether the Kokoro TTS service is available */
  kokoroReady: boolean;

  // ── Settings ───────────────────────────────────────────────────────
  /** Selected TTS voice */
  selectedVoice: string;
  /** TTS speed multiplier */
  speechSpeed: number;
  /** Auto-send transcription (vs. review first) */
  autoSend: boolean;

  // ── Actions ────────────────────────────────────────────────────────
  toggleVoiceMode: () => void;
  setVoiceModeEnabled: (enabled: boolean) => void;
  setSttEnabled: (enabled: boolean) => void;
  setTtsEnabled: (enabled: boolean) => void;

  setIsRecording: (recording: boolean) => void;
  setRecordingDuration: (ms: number) => void;
  setAudioLevel: (level: number) => void;
  setRecordingMode: (mode: RecordingMode) => void;

  setIsSpeaking: (speaking: boolean) => void;
  setPlaybackProgress: (progress: number) => void;

  setVadEnabled: (enabled: boolean) => void;

  setServiceStatus: (whisper: boolean, kokoro: boolean) => void;

  setSelectedVoice: (voice: string) => void;
  setSpeechSpeed: (speed: number) => void;
  setAutoSend: (auto: boolean) => void;
}

export const useVoiceModeStore = create<VoiceModeState>()(
  persist(
    (set) => ({
      // Defaults
      voiceModeEnabled: false,
      sttEnabled: true,
      ttsEnabled: true,
      isRecording: false,
      recordingDuration: 0,
      audioLevel: 0,
      recordingMode: 'toggle',
      isSpeaking: false,
      playbackProgress: 0,
      vadEnabled: false,
      whisperReady: false,
      kokoroReady: false,
      selectedVoice: 'nova',
      speechSpeed: 1.0,
      autoSend: true,

      // Actions
      toggleVoiceMode: () => set((s) => {
        const next = !s.voiceModeEnabled;
        return {
          voiceModeEnabled: next,
          sttEnabled: next ? s.sttEnabled : true,
          ttsEnabled: next ? s.ttsEnabled : true,
        };
      }),
      setVoiceModeEnabled: (enabled) => set({ voiceModeEnabled: enabled }),
      setSttEnabled: (enabled) => set({ sttEnabled: enabled }),
      setTtsEnabled: (enabled) => set({ ttsEnabled: enabled }),

      setIsRecording: (recording) => set({
        isRecording: recording,
        ...(recording ? {} : { recordingDuration: 0, audioLevel: 0 }),
      }),
      setRecordingDuration: (ms) => set({ recordingDuration: ms }),
      setAudioLevel: (level) => set({ audioLevel: Math.max(0, Math.min(1, level)) }),
      setRecordingMode: (mode) => set({ recordingMode: mode }),

      setIsSpeaking: (speaking) => set({
        isSpeaking: speaking,
        ...(speaking ? {} : { playbackProgress: 0 }),
      }),
      setPlaybackProgress: (progress) => set({ playbackProgress: progress }),

      setVadEnabled: (enabled) => set({ vadEnabled: enabled }),

      setServiceStatus: (whisper, kokoro) => set({
        whisperReady: whisper,
        kokoroReady: kokoro,
      }),

      setSelectedVoice: (voice) => set({ selectedVoice: voice }),
      setSpeechSpeed: (speed) => set({ speechSpeed: Math.max(0.5, Math.min(2.0, speed)) }),
      setAutoSend: (auto) => set({ autoSend: auto }),
    }),
    {
      name: 'webelieve-voice-mode',
      version: 3,
      storage: createJSONStorage(() => localStorage),
      // Migrate old localStorage data to new defaults
      migrate: (persisted: any, version: number) => {
        const state = { ...persisted };
        if (version < 2) {
          state.autoSend = true;
        }
        if (version < 3) {
          // v3: Switch from Kokoro voices to OpenAI TTS voices
          const kokoroVoices = ['af_heart', 'af_bella', 'af_nicole', 'af_sarah', 'af_sky', 'am_adam', 'am_michael'];
          if (kokoroVoices.includes(state.selectedVoice)) {
            state.selectedVoice = 'nova';
          }
        }
        return state;
      },
      // Only persist user preferences, not transient state
      partialize: (state) => ({
        voiceModeEnabled: state.voiceModeEnabled,
        sttEnabled: state.sttEnabled,
        ttsEnabled: state.ttsEnabled,
        recordingMode: state.recordingMode,
        vadEnabled: state.vadEnabled,
        selectedVoice: state.selectedVoice,
        speechSpeed: state.speechSpeed,
        autoSend: state.autoSend,
      }),
    }
  )
);
